import classNames from "classnames";
import { SmartGridNode } from "../../build/types";
import { BuilderComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./styles.scss";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import DynamicIcon from "../DynamicIcon";

interface BuilderSmartGridProps extends BuilderComponentProps {
    saveLayout?: {
        editMode: boolean;
        onSave?: () => Promise<void>;
    };
}

const BuilderSmartGrid = ({ node, actionCallbacks, builderProps, children, saveLayout }: BuilderSmartGridProps) => {
    const { id, smartGrid, stateManager, attr, button } = node as SmartGridNode;
    const { columns, rows } = smartGrid || {};
    const { setElement, states } = useInteractionStates<HTMLDivElement>();
    const { setElement: setSmartGridElement, states: smartGridStates } = useInteractionStates<HTMLDivElement>();
    const { setElement: setButtonElement, states: buttonStates } = useInteractionStates<HTMLButtonElement>();

    const smartGridClass = classNames("builder-smart-grid-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    // Extract gap values
    const columnGap = smartGrid?.style?.columnGap || "0px";
    const rowGap = smartGrid?.style?.rowGap || "0px";
    const numColumns = columns || 3;
    const numRows = rows || 3;

    // Calculate column size accounting for gaps: (100% - (n-1) * gap) / n
    const columnSize = numColumns > 1 ? `calc((100% - (${numColumns - 1} * ${columnGap})) / ${numColumns})` : "100%";

    // Calculate row size accounting for gaps: (100% - (m-1) * gap) / m
    const rowSize = numRows > 1 ? `calc((100% - (${numRows - 1} * ${rowGap})) / ${numRows})` : "100%";

    if (builderProps) {
        const { isContainer, nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        if (!children || !Array.isArray(children) || children.length === 0) return null;

        const wrapperStyle = getCompoundStyle({
            node,
            isContainer: isContainer,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
        });

        const gridStyle = getCompoundStyle({
            otherStyle: {
                ...smartGrid?.style,
                gridTemplateColumns: `repeat(${numColumns}, ${columnSize})`,
                gridTemplateRows: `repeat(${numRows}, ${rowSize})`,
                gridAutoColumns: columnSize,
                gridAutoRows: rowSize
            },
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "smartGrid_style")
        });

        const buttonStyle = getCompoundStyle({
            otherStyle: button?.style,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "button_style")
        });

        return (
            <div
                className={smartGridClass}
                style={wrapperStyle}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {saveLayout?.editMode && (
                    <button className="builder-smart-grid-button" style={buttonStyle}>
                        {button?.icon?.src && (
                            <DynamicIcon name={button?.icon?.src} style={button?.icon?.style} colors={button?.icon?.colors} />
                        )}
                        {node.text}
                    </button>
                )}
                <div style={gridStyle} className="builder-smart-grid">
                    {children}
                </div>
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const smartGridWrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({
                node
            }),
            stateManager,
            paths: ["attr_style"]
        });

        const smartGridStyle = getStylesFromInteractiveStates({
            states: smartGridStates,
            defaultStyle: getCompoundStyle({
                otherStyle: {
                    ...smartGrid?.style,
                    gridTemplateColumns: `repeat(${numColumns}, ${columnSize})`,
                    gridTemplateRows: `repeat(${numRows}, ${rowSize})`,
                    gridAutoColumns: columnSize,
                    gridAutoRows: rowSize
                }
            }),
            stateManager,
            paths: ["smartGrid_style"]
        });

        const smartGridButtonStyle = getStylesFromInteractiveStates({
            states: buttonStates,
            defaultStyle: button?.style,
            stateManager,
            paths: ["button_style"]
        });

        return (
            <div {...rest} ref={setElement} className={smartGridClass} style={smartGridWrapperStyle} id={id} {...actionCallbacks}>
                {saveLayout?.editMode && (
                    <button
                        ref={setButtonElement}
                        className="builder-smart-grid-button"
                        style={smartGridButtonStyle}
                        onClick={saveLayout.onSave}
                    >
                        {button?.icon?.src && (
                            <DynamicIcon name={button?.icon?.src} style={button?.icon?.style} colors={button?.icon?.colors} />
                        )}
                        {node.text}
                    </button>
                )}
                <div ref={setSmartGridElement} style={smartGridStyle} className="builder-smart-grid">
                    {children}
                </div>
            </div>
        );
    }
};

export default BuilderSmartGrid;
